import {SelectionModel} from "@angular/cdk/collections";
import {AfterViewInit, Component, computed, Inject, signal, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {PageOffering} from "@core/model/side-nav.model";
import {TabManagementService} from "@core-services/tab-management.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {PaginatorService} from "@shared-services/paginator.service";
import {UtilsService} from "@shared-services/utils.service";
import {ApplicationAssessmentStatus, Retailer, TenantType} from "../../../leads/retailers/retailers.model";
import {LeaseViewComponent} from "../../lease-view/lease-view.component";
import {LeaseViewService} from "../../lease-view/lease-view.service";
import {
  AgreementType,
  LeaseGridSpace,
  LeaseSpaceForTerm,
  LeaseSummary,
  OccupancyType,
  SpaceSelection
} from "../../leasing.model";
import {LeaseCreateMethod} from "./create-a-lease-dialog.interface";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  FixedRateCategory,
  RateType,
  SpaceDefaultRate,
  TimePeriod,
  VariableRateCategory
} from "../../../spaces/model/space-default-pricing.interface";

import {PanelType} from '@shared-components/info-panel/info-panel.component';
import {RetailerLookupComponentOptions} from "@shared-components/retailer-lookup/retailer-lookup.model";

@Component({
  selector: 'app-create-a-lease-dialog',
  templateUrl: './create-a-lease-dialog.component.html',
  styleUrls: ['./create-a-lease-dialog.component.scss']
})
export class CreateALeaseDialogComponent implements AfterViewInit{
  @ViewChild('paginator') paginator!: MatPaginator;

  selectedRetailer!: Retailer;

  dataSource!: MatTableDataSource<LeaseSpaceForTerm>;
  //todo add back defaultRate column
  displayedColumns = ['select', 'id', 'spaceName', 'size'];

  edit = false;
  header: string = '';
  lease!: LeaseSummary;

  selection = new SelectionModel<LeaseGridSpace>(true, []);

  offering!: PageOffering;
  retailerName: FormControl<string | null> = new FormControl(null);
  retailerId?: number | null;

  createMethod!: LeaseCreateMethod;

  leaseRequest!: SpaceSelection;
  leaseForm: FormGroup;

  userId!: number;
  errMessage = '';

  protected readonly OccupancyType = OccupancyType;
  protected allowedRetailerTenantTypes = signal([TenantType.TENANT]);

  retailerLookupOptions = computed<Partial<RetailerLookupComponentOptions>>(() => {
    return {
      allowedRetailerTenantTypes: this.allowedRetailerTenantTypes(),
      allowedCurationStatus: [ApplicationAssessmentStatus.APPROVED]
    }
  });
  PanelType = PanelType;

  pricingMode: 'defaults' | 'custom' | 'none' = 'defaults';
  suggestedRates = {
    fixedPerSqm: null as number | null,
    fixedPerPeriod: null as number | null,
    variable: null as number | null
  };
  allSpacesData: LeaseSpaceForTerm[] = [];

  constructor(public dialogRef: MatDialogRef<CreateALeaseDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: any,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private paginatorService: PaginatorService,
              private leaseViewService: LeaseViewService,
              public loader: LoadingService) {
    this.header = data.header;
    this.lease = data.lease;
    this.offering = data.offering;
    this.createMethod = data.createMethod;

    this.leaseForm = new FormGroup({
      occupancyType: new FormControl<OccupancyType>(this.lease ? data.lease.occupancyType : OccupancyType.SINGLE, Validators.required),
      firstName: new FormControl(null, Validators.required),
      lastName: new FormControl(null, Validators.required),
      email: new FormControl(null, [Validators.required, Validators.email]),
      companyName: new FormControl(null, Validators.required),
      industryTypes: new FormControl<string[]>([], Validators.required),
      startDate: new FormControl(null, [Validators.required]),
      endDate: new FormControl(null, [Validators.required]),

      // new pricing controls
      rentalTimePeriod: new FormControl<TimePeriod | null>(TimePeriod.MONTHLY, Validators.required),
      fixedPerSqmValue: new FormControl<number | null>(null),
      fixedPerSqmDefault: new FormControl<boolean>(false),
      fixedPerPeriodValue: new FormControl<number | null>(null),
      fixedPerPeriodDefault: new FormControl<boolean>(false),
      variablePercentageValue: new FormControl<number | null>(null),
      variableDefault: new FormControl<boolean>(false),
    })
  }

  ngAfterViewInit() {
    switch (this.createMethod) {
      case LeaseCreateMethod.NEW_LEASE_FOR_RETAILER:
        this.leaseForm.get('occupancyType')?.disable();
        this.retailerName.setValue(this.lease.retailerCompanyName!);
        this.retailerId = this.lease.retailerId;
        break;
      case LeaseCreateMethod.EDIT_LEASE:
        this.edit = true;
        this.leaseForm.get('occupancyType')?.disable();
        this.retailerName.setValue(this.lease.retailerCompanyName!);
        this.retailerId = this.lease.retailerId;

        this.leaseForm.get('startDate')?.setValue(new Date(this.lease.leaseStartDate));
        this.leaseForm.get('endDate')?.setValue(new Date(this.lease.leaseEndDate));
        break;
    }

    this.paginator._intl.getRangeLabel = this.paginatorService.getRangeDisplayText;

    this.leaseForm.get('occupancyType')!.valueChanges.subscribe((value) => {
      this.retailerId = null;
      this.retailerName.setValue(null);
      this.allowedRetailerTenantTypes.set([OccupancyType.SHARED === value ? TenantType.OFFERING : TenantType.TENANT]);
    })

    this.leaseForm.get('endDate')?.valueChanges.subscribe(() => this.updateDates());

    // NEW: trigger suggested rates when time period selected or selection changes
    this.leaseForm.get('rentalTimePeriod')?.valueChanges.subscribe(() => this.tryLoadSuggestedRates());
    this.selection.changed.subscribe(() => this.tryLoadSuggestedRates());
  }

  loadSpaces() {
    this.leaseViewService.getDistrictSpacesForDateRange(
      this.dateUtils.displayShortDate(this.leaseForm.get('startDate')?.value)!,
      this.leaseForm.get('endDate')?.value ? this.dateUtils.displayShortDate(this.leaseForm.get('endDate')?.value) : null
    ).subscribe({
      next: value => {
        const availableSpaces = value.leaseSpaces.filter(f => f.availabilityStatus === 'AVAILABLE');
        this.allSpacesData = availableSpaces; // Store full data including defaultPricing
        this.dataSource = new MatTableDataSource<LeaseSpaceForTerm>(availableSpaces);
        this.dataSource.paginator = this.paginator;
      },
      error: err => console.log(err)
    })
  }

  private tryLoadSuggestedRates(): void {
    if (!this.leaseForm.get('rentalTimePeriod')?.value) return;
    const selectedIds = this.selection.selected.map((s: any) => s.districtSpaceId);
    if (!selectedIds.length) return;
    this.processSuggestedRates(selectedIds);
  }

  submitLease() {
    console.log('saving lease: this.selectedRetailer: ', this.selectedRetailer);


    if (this.occupancyType == OccupancyType.SINGLE) {
      let leaseUuid: string | null;
      let retailerId: number | null;

      switch (this.createMethod) {
        case LeaseCreateMethod.EDIT_LEASE:
          leaseUuid = this.lease?.uuid;
          retailerId = this.lease?.retailerId;
          break;
        default:
          leaseUuid = null;
          retailerId = this.selectedRetailer ? this.selectedRetailer.id! : this.lease?.retailerId;
          break;
      }

      const spaceNames = this.selection.selected.map(m => m.spaceName!);

      this.leaseRequest = new SpaceSelection(
        leaseUuid,
        retailerId,
        this.offering.offeringUuid,
        this.occupancyType,
        AgreementType.LEASE,
        this.dateUtils.displayShortDate(this.leaseForm.get('startDate')?.value),
        this.dateUtils.displayShortDate(this.leaseForm.get('endDate')?.value),
        spaceNames,
        this.edit,
        null,
        null
      );

      (this.leaseRequest as any).rentalTimePeriod = this.leaseForm.get('rentalTimePeriod')?.value;
      this.pricingMode = 'none'; //todo - temporarily added this to remove pricing since this dialog will be deprecated anyway
      (this.leaseRequest as any).rentalRates = this.buildRentalRatesPayload();
    }

    console.log('leaseRequest: ', this.leaseRequest);
    this.createLease();
  }

  private processSuggestedRates(selectedSpaceIds: number[]): void {
    const timePeriod: TimePeriod = this.leaseForm.get('rentalTimePeriod')?.value;
    const selectedSpaces = this.allSpacesData.filter(space => selectedSpaceIds.includes(space.districtSpaceId));

    // Collect all rates for the selected time period from selected spaces
    const allRates: SpaceDefaultRate[] = [];
    selectedSpaces.forEach(space => {
      if (space.defaultPricing) {
        const relevantRates = space.defaultPricing.filter(rate => rate.timePeriod === timePeriod);
        allRates.push(...relevantRates);
      }
    });

    // Find the highest rates by category (your business logic for multiple spaces)
    const fixedPerSqmRates = allRates.filter(r => r.rateType === RateType.FIXED && r.category === FixedRateCategory.PER_SQM);
    const fixedPerPeriodRates = allRates.filter(r => r.rateType === RateType.FIXED && r.category === FixedRateCategory.PER_PERIOD);
    const variableRates = allRates.filter(r => r.rateType === RateType.VARIABLE && r.category === VariableRateCategory.PERCENTAGE);

    // Get highest values (or use other business logic)
    this.suggestedRates = {
      fixedPerSqm: fixedPerSqmRates.length ? Math.max(...fixedPerSqmRates.map(r => r.value)) : null,
      fixedPerPeriod: fixedPerPeriodRates.length ? Math.max(...fixedPerPeriodRates.map(r => r.value)) : null,
      variable: variableRates.length ? Math.max(...variableRates.map(r => r.value)) : null
    };

    // Auto-populate custom fields with suggested values
    this.leaseForm.patchValue({
      fixedPerSqmValue: this.suggestedRates.fixedPerSqm,
      fixedPerPeriodValue: this.suggestedRates.fixedPerPeriod,
      variablePercentageValue: this.suggestedRates.variable
    }, { emitEvent: false });

    // Default to 'defaults' mode if suggestions available, otherwise 'custom'
    this.pricingMode = this.hasDefaultsAvailable() ? 'defaults' : 'custom';
  }

  private buildRentalRatesPayload(): SpaceDefaultRate[] {
    if (this.pricingMode === 'none') return [];

    const tp: TimePeriod = this.leaseForm.get('rentalTimePeriod')?.value;
    const out: SpaceDefaultRate[] = [];

    const perSqmValue = this.leaseForm.get('fixedPerSqmValue')?.value;
    if (perSqmValue > 0) {
      out.push({
        spaceId: 0,
        rateType: RateType.FIXED,
        category: FixedRateCategory.PER_SQM,
        timePeriod: tp,
        value: perSqmValue,
        isDefault: false // No longer using individual defaults
      });
    }

    const perPeriodValue = this.leaseForm.get('fixedPerPeriodValue')?.value;
    if (perPeriodValue > 0) {
      out.push({
        spaceId: 0,
        rateType: RateType.FIXED,
        category: FixedRateCategory.PER_PERIOD,
        timePeriod: tp,
        value: perPeriodValue,
        isDefault: false
      });
    }

    const varValue = this.leaseForm.get('variablePercentageValue')?.value;
    if (varValue > 0) {
      out.push({
        spaceId: 0,
        rateType: RateType.VARIABLE,
        category: VariableRateCategory.PERCENTAGE,
        timePeriod: tp,
        value: varValue,
        isDefault: false
      });
    }

    return out;
  }

  createLease() {
    this.leaseViewService.createPlannedLease(this.leaseRequest).subscribe({
      next: value => {
        this.openLeaseViewTab(value.uuid, value.occupancyType!, value.agreementType!);
        this.close();
      },
      error: err => console.log(err)
    });
  }

  updateDates() {
    if (this.leaseForm.get('startDate')?.value && this.leaseForm.get('endDate')?.value) {
      this.loadSpaces();
    }
  }

  selectRetailer(event: any) {
    this.selectedRetailer =  event;
  }

  close() {
    this.dialogRef.close();
  }

  openLeaseViewTab(id: string, occupancyType: OccupancyType, agreementType: AgreementType) {
    const payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(id),
      pageHeader: this.offering.label,
      data: { id, occupancyType, agreementType },
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  disableRetailerSelection(): boolean {
    return !!this.lease && !!this.lease.retailerId && !!this.lease.retailerCompanyName;
  }

  get occupancyType(): OccupancyType {
    return this.leaseForm.get('occupancyType')?.value;
  }

  hasDefaultsAvailable(): boolean {
    return !!(this.suggestedRates.fixedPerSqm || this.suggestedRates.fixedPerPeriod || this.suggestedRates.variable);
  }
}
